import { Component, ElementRef, inject, input, model, signal, ChangeDetectionStrategy } from '@angular/core';
import { GsStructTarget } from '../../models';
import { SystemService } from '../../services';
import { GsDropdownMenuItem } from './models/dropdown-item';

/**
 * The controller for a dropdown-menu
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <ng-content></ng-content>
  `
})
export class GsDropdownController {
  // Services
  private readonly system = inject(SystemService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  // Inputs & Models
  /** All items/links inside the dropdown-list */
  readonly items = input<GsDropdownMenuItem[]>([]);
  /** Is this dropdown currently opened? */
  readonly opened = model(false);
  /** Should the dropdown be displayed inline and not as an extra floating container? */
  readonly isInline = input(false);
  /** Should the submenus dropdown be opened inline and not as an extra floating container? */
  readonly openSubmenusInline = input(false);
  /** Is this dropdown-menu a submenu of another dropdown? */
  readonly isSub = input(false);
  /** Reference to the parent menu item when this controller renders a submenu */
  readonly parentItem = input<GsDropdownMenuItem | null>(null);
  /** Element whose bottom edge the root menu opens beneath (e.g. the header nav), defaults to the trigger */
  readonly anchor = input<HTMLElement | null>(null);
  /** Custom aria-label for the dropdown menu for better accessibility */
  readonly ariaLabel = input<string>();

  // Properties & Signals
  /** The initial absolute position of the dropdown from the top */
  protected readonly initialPositionTop = signal(0);
  /** The initial absolute position of the dropdown from the left */
  protected readonly initialPositionLeft = signal(0);
  /** Height of the gap between trigger and anchored menu, bridged so hovering across it keeps the menu open */
  protected readonly hoverBridgeHeight = signal(0);
  /** Width of that bridge: only the trigger's, so it never covers neighbouring header content */
  protected readonly hoverBridgeWidth = signal(0);
  /** Reference to the element that triggered the dropdown for focus restoration */
  protected readonly triggerElement = signal<HTMLElement | null>(null);

  // Methods
  /**
   * Toggles the dropdown open/closed
   * @param state new state of the dropdown
   * @param event the event that triggered the toggle (click)
   * @param menuItem the menu item to toggle
   * @param viaKeyboard whether the toggle was triggered via keyboard (for auto-focus behavior)
   */
  toggleDropdown(state?: boolean, event?: Event, menuItem?: GsDropdownMenuItem, viaKeyboard = false): void {
    const newState = state ?? !this.opened();

    // Store trigger element when opening for focus restoration on Escape
    if (newState) {
      if (event) {
        const target = event.target as HTMLElement;
        // Try to find a focusable ancestor (button, link, or any focusable element)
        this.triggerElement.set((target.closest('a, button, [tabindex]') as HTMLElement) ?? target);
      } else {
        // Fallback to currently focused element when opened programmatically
        this.triggerElement.set(document.activeElement as HTMLElement);
      }
    }

    if (event) {
      const position = this.computeTriggerPosition(event.target as HTMLElement);
      this.initialPositionLeft.set(position.left);
      this.initialPositionTop.set(position.top);
      this.hoverBridgeHeight.set(position.hoverBridgeHeight);
      this.hoverBridgeWidth.set(position.hoverBridgeWidth);
    }

    this.closeAllSubs();
    if (menuItem) {
      menuItem.opened = newState;
    }
    this.opened.set(newState);

    if (newState && viaKeyboard) {
      setTimeout(() => {
        const menuItems = this.getFirstMenuItems();
        if (menuItems && menuItems.length > 0) {
          menuItems[0].focus();
        }
      });
    }
  }

  /**
   * Toggles a submenu open/closed (for chevron buttons that only toggle submenus)
   * Handles both mouse clicks and keyboard events consistently
   * @param item the menu item with submenu to toggle
   * @param event the triggering event (click or keyboard)
   */
  toggleSubmenu(item: GsDropdownMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.items.length > 0) {
      this.toggleSubItem(item);
    }
  }

  /**
   * Handles activation (click/Enter/Space) on a dropdown item
   * Navigates to link if present, otherwise toggles submenu
   * @param item the activated item
   * @param event the triggering event (click or keyboard)
   */
  activateItem(item: GsDropdownMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // If item has a link, navigate to it (external links always open in new tab)
    if (item.linkUrl) {
      const isExternal = item.linkUrl.startsWith('http');
      const target = isExternal || item.linkTarget === '_blank' ? GsStructTarget._blank : GsStructTarget._self;
      this.system.openLink(item.linkUrl, target);
    }
    // If item has sub-items, toggle the submenu
    else if (item.items.length > 0) {
      this.toggleSubItem(item);
    }
  }

  /**
   * Checks if a dropdown item's link matches the current page URL
   * @param item The dropdown menu item to check
   * @returns true if the item's linkUrl matches the current relative URL
   */
  isItemActive(item: GsDropdownMenuItem): boolean {
    return !!item.linkUrl && item.linkUrl === this.system.currentRelativeLink();
  }

  /**
   * Handles mouse enter event on a dropdown item.
   * @param item The dropdown item being hovered.
   */
  mouseentered(item?: GsDropdownMenuItem): void {
    this.closeAllSubs();
    if (item && item.items.length > 0 && !item.opened) {
      item.opened = true;
    }
  }

  /**
   * Handles focus leaving the dropdown menu.
   * Closes the dropdown when focus moves to an element outside the menu.
   * Returns early for inline dropdowns and inline submenus, since their visibility
   * is governed by the parent and they must not auto-close on focusout.
   * @param event The focusout event
   */
  handleFocusout(event: FocusEvent): void {
    // Inline dropdowns (e.g. the mobile sidebar) are always-visible containers
    // whose visibility is governed by the parent — never auto-close them on focusout.
    if (this.isInline() || (this.isSub() && this.openSubmenusInline())) {
      return;
    }

    const menu = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    // Check if focus is moving to an element outside this menu
    // relatedTarget is null when focus moves to non-focusable element or outside document
    if (!relatedTarget || !menu.contains(relatedTarget)) {
      this.toggleDropdown(false);
    }
  }

  /**
   * Handles keyboard navigation within the dropdown menu (WCAG 2.1 AAA)
   * @param event The keyboard event
   */
  handleKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const currentMenu = target.closest('[role="menu"]') as HTMLElement | null;
    const menuItems = this.getMenuItems(currentMenu);
    const menuItemTarget = target.closest('[role="menuitem"]') as HTMLElement;
    const currentIndex = menuItems.indexOf(menuItemTarget);

    // Handle Escape to close dropdown and return focus to trigger
    if (event.key === 'Escape') {
      event.preventDefault();
      this.toggleDropdown(false);
      this.triggerElement()?.focus();
      return;
    }

    // Tab is handled naturally - focusout will close the dropdown when focus leaves

    // Guard: If no menu items or target not found, focus first item for navigation keys
    if (menuItems.length === 0) {
      return;
    }

    const validIndex = currentIndex >= 0 ? currentIndex : 0;
    const currentItems = this.items();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (validIndex < menuItems.length - 1) {
          menuItems[validIndex + 1].focus();
        } else {
          menuItems[0].focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (validIndex > 0) {
          menuItems[validIndex - 1].focus();
        } else {
          menuItems[menuItems.length - 1].focus();
        }
        break;
      case 'ArrowRight':
        if (menuItemTarget?.getAttribute('aria-haspopup') === 'menu') {
          event.preventDefault();
          const item = currentItems[validIndex];
          if (item && item.items.length > 0) {
            if (!item.opened) {
              this.closeAllSubs();
              item.opened = true;
            }
            this.focusFirstItemInChildMenu(menuItemTarget);
          }
        }
        break;
      case 'ArrowLeft':
        if (this.isSub()) {
          event.preventDefault();
          this.toggleDropdown(false, undefined, this.parentItem() ?? undefined);
          this.focusParentMenuItem();
        }
        break;
      case 'Home':
        event.preventDefault();
        menuItems[0].focus();
        break;
      case 'End':
        event.preventDefault();
        menuItems[menuItems.length - 1].focus();
        break;
    }
  }

  /**
   * Calculates the top position of the dropdown
   * @returns the top position of the dropdown
   */
  protected topPositionStyle(): string {
    if (this.isSub() && !this.openSubmenusInline()) {
      return '0px';
    }

    return `${this.initialPositionTop()}px`;
  }

  /**
   * Calculates the left position of the dropdown
   * @returns the left position of the dropdown
   */
  protected leftPositionStyle(): string {
    if (this.isSub() && !this.openSubmenusInline()) {
      return '100%';
    }

    return `${this.initialPositionLeft()}px`;
  }

  /**
   * Gets focusable menu items from the first dropdown menu container in the DOM
   * @returns An array of focusable menu item elements (empty if no container or items are found)
   */
  private getFirstMenuItems(): HTMLElement[] {
    const host = this.elementRef.nativeElement;
    const menuContainer: HTMLElement | null = host.querySelector('[role="menu"]') ?? host.querySelector('ul');
    if (!menuContainer) {
      return [];
    }
    return this.getMenuItems(menuContainer);
  }

  /**
   * Gets menu items for keyboard navigation by querying the DOM directly
   * @param menuElement The ul/div element containing the menu items
   * @returns Array of focusable menu item elements
   */
  private getMenuItems(menuElement: HTMLElement | null): HTMLElement[] {
    if (!menuElement) {
      return [];
    }
    let items = Array.from(menuElement.querySelectorAll<HTMLElement>(':scope > li > [role="menuitem"]'));
    if (items.length === 0) {
      items = Array.from(menuElement.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')).filter((el) => {
        const closestMenu = el.closest('ul, [role="menu"], [role="menubar"]') as HTMLElement | null;
        return closestMenu === menuElement;
      });
    }
    return items;
  }

  /**
   * Closes all submenus
   * @param items the items to close
   */
  private closeAllSubs(items: GsDropdownMenuItem[] = this.items()): void {
    items.forEach((item) => {
      if (!item.items || item.items.length === 0) return;
      item.opened = false;
      if (item.items) {
        this.closeAllSubs(item.items);
      }
    });
  }

  /**
   * Toggles a single submenu and closes all others
   * @param item item to toggle
   */
  private toggleSubItem(item: GsDropdownMenuItem): void {
    const newState = !item.opened;
    this.closeAllSubs();
    item.opened = newState;
  }

  /**
   * Calculates the menu origin below the trigger, or below the anchor's bottom edge when one is set.
   * Coordinates are relative to the trigger's offset parent, which is the menu's containing block as well.
   * @param target The element that triggered the dropdown
   * @returns The menu position and the size of the gap between trigger and menu
   */
  private computeTriggerPosition(target: HTMLElement): {
    left: number;
    top: number;
    hoverBridgeHeight: number;
    hoverBridgeWidth: number;
  } {
    const triggerBottom = target.offsetTop + target.offsetHeight;
    const anchor = this.isSub() ? null : this.anchor();
    const offsetParent = target.offsetParent;
    if (!anchor || !offsetParent) {
      return { left: target.offsetLeft, top: triggerBottom, hoverBridgeHeight: 0, hoverBridgeWidth: 0 };
    }

    const anchorBottom = anchor.getBoundingClientRect().bottom - offsetParent.getBoundingClientRect().top - offsetParent.clientTop;
    const top = Math.max(anchorBottom, triggerBottom);
    return { left: target.offsetLeft, top, hoverBridgeHeight: top - triggerBottom, hoverBridgeWidth: target.offsetWidth };
  }

  /**
   * Focuses the first focusable item inside the submenu that belongs to the provided menu trigger
   * @param menuTrigger The menu item that owns the submenu
   */
  private focusFirstItemInChildMenu(menuTrigger: HTMLElement): void {
    setTimeout(() => {
      const submenuElement = this.getChildMenuElement(menuTrigger);
      if (!submenuElement) {
        return;
      }

      const submenuItems = this.getMenuItems(submenuElement);
      if (submenuItems.length > 0) {
        submenuItems[0].focus();
      }
    });
  }

  /**
   * Finds the child submenu container (ul[role="menu"]) for a given trigger element
   * @param menuTrigger The menu item that owns the submenu
   * @returns The submenu container, or null if the item has no rendered submenu
   */
  private getChildMenuElement(menuTrigger: HTMLElement): HTMLElement | null {
    const parentListItem = menuTrigger.closest('li');
    if (!parentListItem) {
      return null;
    }

    const childDropdownHost = Array.from(parentListItem.children).find(
      (element): element is HTMLElement => element instanceof HTMLElement && element.tagName.toLowerCase() === 'dropdown'
    );
    if (!childDropdownHost) {
      return null;
    }

    return childDropdownHost.querySelector<HTMLElement>('[role="menu"]');
  }

  /**
   * Returns focus to the parent menu trigger when navigating back from a submenu
   */
  private focusParentMenuItem(): void {
    const hostElement = this.elementRef.nativeElement;
    const parentTrigger = hostElement.previousElementSibling as HTMLElement | null;

    if (parentTrigger) {
      parentTrigger.focus();
      this.triggerElement.set(parentTrigger);
      return;
    }

    this.triggerElement()?.focus();
  }
}
